/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `reportOnScreenLoaders` for the Caintra Connect frontend.
 * @module logic/reports/reportOnScreenLoaders
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../../utils/logger.js";
import { obtainBuyerAssignmentsReport } from "./index.js";
import {
  getCustomerCompanyUsersBasicInfoByEventId,
  getCustomerCompanyUsersBasicInfoByRFC,
} from "../customerCompanyUser";

export const loadTablesReportOnScreen = async ({
  selectedEvent,
  navigate,
  setSalaLayouts,
  setSelectedEvent,
  setLoadingLayout,
  notifyExportError,
}) => {
  if (!selectedEvent) {
    setSalaLayouts([]);
    setSelectedEvent(null);
    return;
  }

  setLoadingLayout(true);
  try {
    const reportData = await obtainBuyerAssignmentsReport(
      selectedEvent._id,
      navigate,
    );
    setSalaLayouts(reportData);
  } catch (err) {
    logger.error("Error", { err: sanitizeError(err) });
    setSalaLayouts([]);
    notifyExportError(err, "FORBIDDEN");
  } finally {
    setLoadingLayout(false);
  }
};

const loadBuyerCompaniesBySelection = async ({
  selectedBuyerCompanies,
  selectedEvent,
  navigate,
  setBuyerCompaniesData,
  setLoadingLayout,
}) => {
  if (selectedBuyerCompanies?.length > 0) {
    try {
      const buyerCompaniesData = await Promise.all(
        selectedBuyerCompanies.map(async (buyerCompany) => {
          const buyerCompanyUsers =
            await getCustomerCompanyUsersBasicInfoByRFC(
              buyerCompany.RFC,
              navigate,
            );
          return {
            ...buyerCompany,
            customerCompanyUsers: buyerCompanyUsers,
          };
        }),
      );
      setBuyerCompaniesData(buyerCompaniesData);
    } catch (err) {
      logger.error("Error", { err: sanitizeError(err) });
      setBuyerCompaniesData([]);
    } finally {
      setLoadingLayout(false);
    }
    return;
  }

  if (selectedEvent) {
    try {
      const buyerCompanyUsers = await getCustomerCompanyUsersBasicInfoByEventId(
        selectedEvent._id,
        navigate,
      );
      setBuyerCompaniesData(buyerCompanyUsers);
    } catch (err) {
      logger.error("Error", { err: sanitizeError(err) });
      setBuyerCompaniesData([]);
    } finally {
      setLoadingLayout(false);
    }
    return;
  }

  setLoadingLayout(false);
};

export const loadBuyerReportOnScreen = async ({
  selectedEvent,
  selectedBuyerCompanies,
  navigate,
  setBuyerCompaniesData,
  setLoadingLayout,
}) => {
  if (!selectedEvent && selectedBuyerCompanies?.length === 0) {
    logger.error("Error generating report on screen: Missing required fields");
    return;
  }

  setLoadingLayout(true);
  await loadBuyerCompaniesBySelection({
    selectedBuyerCompanies,
    selectedEvent,
    navigate,
    setBuyerCompaniesData,
    setLoadingLayout,
  });
};

/**
 * Generates on-screen report data for supported report types.
 * @param {number|undefined} reportValue - Selected report option value
 * @param {Object} deps - State setters and current selections
 */
export const generateReportOnScreenForOption = async (reportValue, deps) => {
  if (reportValue === 0) {
    await loadTablesReportOnScreen(deps);
    return;
  }
  if (reportValue === 2) {
    await loadBuyerReportOnScreen(deps);
    return;
  }
  // Unsupported on-screen report type. No action required.
};
